import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@adiox/api';
import { Modal, useApi, useSession } from '@adiox/ui';
import { useActiveLogin } from '../trading/useTrading';
import { useStream } from '../stream/stream';
import { usePaymentsDialog } from './paymentsDialogStore';

type PaymentView = components['schemas']['PaymentView'];

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';

const problemOf = async (response: Response | undefined, error: unknown) => {
  const detail = (error as { detail?: string } | undefined)?.detail;
  return (
    detail ?? (response ? `The request failed (${response.status}).` : 'The server could not be reached.')
  );
};

/**
 * Deposits and withdrawals: the client asks, and a manager handles it (the balance changes once they complete it). Lists the
 * account's requests with their state; one nobody has taken yet can be cancelled.
 */
export function PaymentsDialog() {
  const open = usePaymentsDialog((s) => s.open);
  const hide = usePaymentsDialog((s) => s.hide);
  const login = useActiveLogin();
  const readOnly = useSession().user?.readOnly === true;
  return (
    <Modal open={open} title="Deposit and withdrawal" onClose={hide} size="md">
      {open &&
        (login === undefined ? (
          <p className="text-sm text-muted">No trading account.</p>
        ) : readOnly ? (
          <p className="text-sm text-muted">
            This is a read-only (investor) session: payments are asked for by the account owner.
          </p>
        ) : (
          <Payments login={login} />
        ))}
    </Modal>
  );
}

function Payments({ login }: { login: number }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const account = useStream((s) => s.accounts[login]);
  const [action, setAction] = useState<'Deposit' | 'Withdrawal'>('Deposit');
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [userAccount, setUserAccount] = useState('');
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const wallets = useQuery({
    queryKey: ['payment-wallets'],
    staleTime: 300_000,
    queryFn: async () => (await api.GET('/api/v1/payments/wallets')).data ?? { wallets: [], minAmount: 1 },
  });
  const mine = useQuery({
    queryKey: ['my-payments', login],
    refetchInterval: 15_000,
    queryFn: async () =>
      (await api.GET('/api/v1/accounts/{login}/payments', { params: { path: { login } } })).data ?? [],
  });
  const chosenWallet = wallet || wallets.data?.wallets[0] || '';
  const currency = account?.currency ?? '';

  const submit = async () => {
    setBusy(true);
    setMessage(null);
    const { data, error, response } = await api.POST('/api/v1/accounts/{login}/payments', {
      params: { path: { login } },
      body: { action, amount: Number(amount), wallet: chosenWallet, userAccount, comment },
    });
    setBusy(false);
    if (!data) return setMessage({ tone: 'error', text: await problemOf(response, error) });
    setAmount('');
    setComment('');
    setMessage({
      tone: 'ok',
      text: `${action} #${data.id} sent. A manager will handle it; the balance changes once it is done.`,
    });
    void queryClient.invalidateQueries({ queryKey: ['my-payments', login] });
  };

  const cancel = async (p: PaymentView) => {
    const { error, response } = await api.DELETE('/api/v1/accounts/{login}/payments/{id}', {
      params: { path: { login, id: p.id } },
    });
    if (response && !response.ok) setMessage({ tone: 'error', text: await problemOf(response, error) });
    void queryClient.invalidateQueries({ queryKey: ['my-payments', login] });
  };

  return (
    <div className="flex flex-col gap-4">
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <fieldset className="sm:col-span-2">
          <legend className={label}>What</legend>
          <div className="flex gap-4 text-sm">
            {(['Deposit', 'Withdrawal'] as const).map((a) => (
              <label key={a} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="payment-action"
                  checked={action === a}
                  onChange={() => setAction(a)}
                />
                {a}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label className={label} htmlFor="payment-amount">
            Amount{currency ? ` (${currency})` : ''}
          </label>
          <input
            id="payment-amount"
            className={`${field} num`}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`At least ${wallets.data?.minAmount ?? 1}`}
          />
          {action === 'Withdrawal' && account?.loaded && (
            <p className="mt-1 text-xs text-muted">
              Free margin: {account.figures.freeMargin.toFixed(2)} {currency}
            </p>
          )}
        </div>
        <div>
          <label className={label} htmlFor="payment-wallet">
            Payment method
          </label>
          <select
            id="payment-wallet"
            className={field}
            value={chosenWallet}
            onChange={(e) => setWallet(e.target.value)}
          >
            {(wallets.data?.wallets ?? []).map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={label} htmlFor="payment-user-account">
            {action === 'Withdrawal' ? 'Send to (your account or wallet address)' : 'Paying from (optional)'}
          </label>
          <input
            id="payment-user-account"
            className={field}
            maxLength={128}
            value={userAccount}
            onChange={(e) => setUserAccount(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={label} htmlFor="payment-comment">
            Comment (optional)
          </label>
          <input
            id="payment-comment"
            className={field}
            maxLength={256}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button
            type="submit"
            disabled={busy || !amount || !chosenWallet || (action === 'Withdrawal' && !userAccount.trim())}
            className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-accent-fg disabled:opacity-40"
          >
            Ask for the {action.toLowerCase()}
          </button>
          {message && (
            <p
              role={message.tone === 'error' ? 'alert' : 'status'}
              className={`text-sm ${message.tone === 'error' ? 'text-down' : 'text-up'}`}
            >
              {message.text}
            </p>
          )}
        </div>
      </form>

      <section aria-label="Your payments">
        <h3 className="mb-1 text-sm font-semibold">Your requests</h3>
        {(mine.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">None yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                <th className="py-1 font-normal">#</th>
                <th className="font-normal">What</th>
                <th className="text-right font-normal">Amount</th>
                <th className="font-normal">State</th>
                <th className="font-normal">Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(mine.data ?? []).map((p) => (
                <tr key={p.id} className="border-t border-line/40" data-testid="my-payment">
                  <td className="num py-1">{p.id}</td>
                  <td>
                    {p.action} · {p.wallet}
                  </td>
                  <td className="num text-right">
                    {p.amount.toFixed(2)} {p.currency}
                  </td>
                  <td className={p.state === 'Done' ? 'text-up' : p.state === 'Rejected' ? 'text-down' : ''}>
                    {p.state === 'Locked' || p.state === 'Processing' ? 'Being handled' : p.state}
                  </td>
                  <td className="text-muted">
                    {p.state === 'Rejected' ? p.description : p.state === 'Done' ? p.transactionId : ''}
                  </td>
                  <td className="text-right">
                    {p.state === 'Waiting' && (
                      <button
                        type="button"
                        className="text-xs text-down hover:underline"
                        onClick={() => void cancel(p)}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
